import React, { useState, useEffect } from 'react';
import { connectAuthPost } from './connectApi';
import './styles/PostComments.css';

/**
 * Expandable comment section under a post.
 * Loads comments on mount, supports add + delete-own.
 * onCountChange(postId, newCount) keeps the parent's counter in sync.
 */
export default function PostComments({ postId, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await connectAuthPost('connect/comments/list', { post_id: postId });
        if (!cancelled && result.success) {
          setComments(result.data.comments);
        }
      } catch (err) {
        console.error('Failed to load comments:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [postId]);

  const handleAdd = async () => {
    const content = newComment.trim();
    if (!content || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await connectAuthPost('connect/comments/create', {
        post_id: postId,
        content
      });
      if (result.success) {
        setComments(prev => [...prev, result.data.comment]);
        setNewComment('');
        onCountChange?.(postId, result.data.comments);
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      const result = await connectAuthPost('connect/comments/delete', { comment_id: commentId });
      if (result.success) {
        setComments(prev => {
          const next = prev.filter(c => c.id !== commentId);
          onCountChange?.(postId, next.length);
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const formatTime = (timestamp) => {
    const diff = Date.now() - timestamp * 1000;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  return (
    <div className="connect-comments">
      {isLoading ? (
        <p className="connect-comments-loading">Loading comments...</p>
      ) : (
        <>
          {comments.length === 0 && (
            <p className="connect-comments-empty">No comments yet. Be the first!</p>
          )}
          {comments.map(comment => (
            <div key={comment.id} className="connect-comment">
              <div className="connect-comment-avatar">
                {comment.user_picture ? (
                  <img src={comment.user_picture} alt={comment.user_name} />
                ) : (
                  <span>{comment.user_name ? comment.user_name.charAt(0).toUpperCase() : '?'}</span>
                )}
              </div>
              <div className="connect-comment-body">
                <div className="connect-comment-head">
                  <span className="connect-comment-author">{comment.user_name}</span>
                  <span className="connect-comment-time">{formatTime(comment.timestamp)}</span>
                  {comment.is_own && (
                    <button
                      className="connect-comment-delete"
                      onClick={() => handleDelete(comment.id)}
                      title="Delete comment"
                    >
                      ×
                    </button>
                  )}
                </div>
                <p className="connect-comment-content">{comment.content}</p>
              </div>
            </div>
          ))}
        </>
      )}

      <div className="connect-comment-input-row">
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="Write a comment..."
          maxLength={1000}
        />
        <button
          onClick={handleAdd}
          disabled={isSubmitting || !newComment.trim()}
        >
          {isSubmitting ? '...' : 'Post'}
        </button>
      </div>
    </div>
  );
}
